import { useLazyQuery, useMutation } from '@apollo/client'
import { ProjectElementNode } from '@deltek/specification-client/core/Models/productListingDetail'
import GridContainer from '@deltek/specification-client/core/Surfaces/GridContainer'
import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import {
    CircularProgress,
    makeStyles,
    Menu,
    MenuItem,
    Theme,
    Typography,
} from '@material-ui/core'
import CheckIcon from '@material-ui/icons/Check'
import ChevronRightIcon from '@material-ui/icons/ChevronRight'
import { UpdateProjectFamilyIterationMutation } from 'api/graphQL/mutations/projectElement.mutations'
import {
    GetProductFamilyIterationQuery,
    GetVersionHasTrackChangeQuery,
} from 'api/graphQL/queries/projectElements.queries'
import { ElementType } from 'api/graphQL/reactiveVars/element'
import {
    reactiveWorkspaceContents,
    setReactiveWorkspaceContents,
} from 'api/graphQL/reactiveVars/workspaceContent'
import LoadingBackdrop from 'bpm/components/loadingBackdrop/LoadingBackdrop'
import ConfirmationDialog from 'components/ConfirmationDialog/ConfirmationDialog'
import {
    UpdateProjectFamilyIterationResponse,
    UpdateProjectFamilyIterationsInput,
} from 'Models/project'
import { ArchitectEngineeringFamilyIteration } from 'models/projectElementNode'
import React from 'react'
import useGlobalSnackbar from 'shared/utils/useGlobalSnackbar'

const useStyles = makeStyles((theme: Theme) => ({
    versionMenu: {
        '& > .MuiBackdrop-root': {
            zIndex: 1,
        },
    },
    versionMenuSelector: {
        cursor: 'pointer',
        '& > .MuiSvgIcon-root': {
            fontSize: pxToRem(20),
        },
    },
    labelText: {
        fontSize: pxToRem(14),
        fontFamily: 'Roboto',
        color: '#211F20',
    },
    menuContainer: {
        '&>.MuiMenu-paper': {
            '&>.MuiMenu-list': {
                width: pxToRem(200),
            },
        },
    },
    check: {
        position: 'absolute',
        left: pxToRem(10),
        '& > svg': {
            fontSize: pxToRem(20),
        },
    },
    iterationName: {
        marginLeft: theme.spacing(2.5),
        fontSize: pxToRem(14),
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
}))

const dialogMessage =
    'You have edited the content contained in the office master. If you switch to another office master, the edits you have made here will be lost.'

/** Represents state and functions for WorkspaceVersionDropdown component */
interface WorkspaceVersionDropdownProps {
    /** The id of the node element. */
    id?: string
    /** The baseElementId of the node element. */
    baseElementId?: string
    /** The elementId of the node element. */
    elementId?: string
    /** The id of the current project loaded. */
    projectId?: string
    /** The elementType of the node element. */
    elementType?: string
    /** Indicates whether the element node is expanded. */
    isExpanded?: boolean
    /** The familyElementNodeId of the node element. */
    familyElementNodeId?: string
    /** Function to update the current or new node values. */
    updateElementList?: (
        elementList: ProjectElementNode[],
        parentElementNodes?: ProjectElementNode[],
        deletedElementNodes?: ProjectElementNode[],
        appendElements?: boolean,
        selectedElementFromDialog?: string,
        isUpdateAfterVersionChange?: boolean
    ) => void
    /** The selectedElementFormat of the node element. */
    selectedElementFormat?: string
    /** The parentNodeId of the node element. */
    parentNodeId?: string | null
    /** Function to show and hide action popover. */
    setIsActionPopoverDisplayed: (value: boolean) => void
    /** The baseElementId of the node element. */
    treePath?: string
    /** The searchText of the node element searched. */
    searchText?: string
    /** Indicates whether the element node is currently disabled. */
    isDisabled?: boolean
    /** Indicates whether the component is rendered from AE workspace. */
    isFromWorkSpacePage?: boolean
}

const WorkspaceVersionDropdown = ({
    id,
    baseElementId,
    elementId,
    projectId,
    elementType,
    isExpanded,
    familyElementNodeId,
    updateElementList,
    selectedElementFormat,
    parentNodeId,
    setIsActionPopoverDisplayed,
    treePath,
    searchText,
    isDisabled = true,
    isFromWorkSpacePage = false,
}: WorkspaceVersionDropdownProps) => {
    const classes = useStyles()

    const { availableVersions } = reactiveWorkspaceContents()

    const { showGlobalSnackbar } = useGlobalSnackbar()

    const [
        isMenuDisplayed,
        setIsMenuDisplayed,
    ] = React.useState<Element | null>(null)

    const [hasTrackChange, setHasTrackChange] = React.useState<boolean>(false)

    const [currentIteration, setCurrentIteration] = React.useState<Partial<
        ArchitectEngineeringFamilyIteration
    > | null>(null)

    const [showCautionDialog, setShowCautionDialog] = React.useState<boolean>(
        false
    )

    const canUpdate = React.useMemo(() => {
        const version = availableVersions?.find(
            (version) => version.familyBaseElementId === baseElementId
        )
        return elementType === ElementType.PRODUCTFAMILY && !version
    }, [availableVersions, baseElementId])

    const { familyNumber, familyName } = React.useMemo(() => {
        if (!id) return { familyNumber: '', familyName: '' }
        const parts = id.split('||')
        const lastPart = parts[parts.length - 1] || ''
        const details = lastPart.split('|')
        return {
            familyNumber: details[0] || '',
            familyName: details[1] || '',
        }
    }, [id])

    const [
        getVersionHasTrackChangeQuery,
        { loading: hasTrackChangeLoading },
    ] = useLazyQuery(GetVersionHasTrackChangeQuery, {
        fetchPolicy: 'no-cache',
        nextFetchPolicy: 'cache-first',
        notifyOnNetworkStatusChange: true,
        onCompleted: (data) => {
            if (data) {
                setHasTrackChange(data.hasTrackChanges)
            }
        },
    })

    const [
        getProductFamilyIterationQuery,
        { loading: familyIterationLoading },
    ] = useLazyQuery(GetProductFamilyIterationQuery, {
        fetchPolicy: 'no-cache',
        nextFetchPolicy: 'cache-first',
        notifyOnNetworkStatusChange: true,
        onCompleted: (data) => {
            if (data) {
                setReactiveWorkspaceContents({
                    availableVersions:
                        data.getProductFamilyIterations?.familyIterations,
                })
            }
        },
    })

    const [
        updateProjectFamilyIteration,
        { loading: updateVersionLoading },
    ] = useMutation<
        UpdateProjectFamilyIterationResponse,
        UpdateProjectFamilyIterationsInput
    >(UpdateProjectFamilyIterationMutation, {
        onCompleted: (data) => {
            setIsActionPopoverDisplayed(false)
            updateElementList?.(
                data?.updateProjectFamilyIteration as ProjectElementNode[],
                [],
                [],
                true,
                undefined,
                true
            )
            showGlobalSnackbar({
                variables: {
                    message: 'Updated Family Version Successfully',
                    variant: 'success',
                    id: `FamilyIteration_UpdateSuccess`,
                },
            })
        },
        onError: () => {
            showGlobalSnackbar({
                variables: {
                    message: 'Family Version Update Failed',
                    variant: 'error',
                    id: `FamilyIteration_UpdateError`,
                },
            })
        },
    })

    React.useEffect(() => {
        if (canUpdate) {
            getVersionHasTrackChangeQuery({
                variables: {
                    input: {
                        familyElementId: elementId!,
                    },
                },
            })
            getProductFamilyIterationQuery({
                variables: {
                    input: {
                        projectId,
                        familyNumber,
                        familyName,
                        treePath,
                        ...(isFromWorkSpacePage && familyElementNodeId && { familyElementNodeId }),
                    },
                },
            })
        }
    }, [availableVersions, familyNumber, familyName, projectId, treePath, familyElementNodeId, isFromWorkSpacePage])

    const handleVersionMenuDisplay = React.useCallback(
        (event) => {
            if (isDisabled || familyIterationLoading || hasTrackChangeLoading)
                return
            setIsMenuDisplayed(event.currentTarget)
        },
        [
            isDisabled,
            isMenuDisplayed,
            familyIterationLoading,
            hasTrackChangeLoading,
        ]
    )

    const handleMenuClick = React.useCallback(
        (iteration) => () => {
            if (hasTrackChange) {
                setCurrentIteration(iteration)
                return setShowCautionDialog(true)
            }
            handleVersionUpdate(iteration)()
        },
        [currentIteration, hasTrackChange]
    )

    const handleVersionUpdate = React.useCallback(
        ({ iterationId, isSelected }) => () => {
            setShowCautionDialog(false)
            if (isSelected) return
            const selectedElementFormatLocal = selectedElementFormat
                ? selectedElementFormat === 'ASSEMBLIES'
                    ? 'ASSEMBLY'
                    : 'DIVISION'
                : 'ASSEMBLY'
            updateProjectFamilyIteration({
                variables: {
                    input: {
                        projectId: projectId!,
                        familyNumber,
                        iterationId: iterationId,
                        familyElementNodeId: familyElementNodeId!,
                        isExpanded: isExpanded!,
                        elementListView: selectedElementFormatLocal,
                        parentNodeId: parentNodeId!,
                        searchText,
                    },
                },
            })
            setIsMenuDisplayed(null)
        },
        [
            familyNumber,
            projectId,
            isExpanded,
            currentIteration,
            showCautionDialog,
            selectedElementFormat,
        ]
    )

    const handleClose = React.useCallback(() => {
        setIsMenuDisplayed(null)
    }, [isMenuDisplayed])

    const handleDialogClose = React.useCallback(() => {
        setShowCautionDialog(false)
    }, [showCautionDialog])

    return (
        <div className={classes.versionMenu}>
            <GridContainer
                data-testid="versionMenu"
                justifyContent="space-between"
                alignItems="center"
                onClick={handleVersionMenuDisplay}
                className={classes.versionMenuSelector}
            >
                <Typography component="span" className={classes.labelText}>
                    Office Master Selection
                </Typography>
                {familyIterationLoading || hasTrackChangeLoading ? (
                    <CircularProgress size={15} />
                ) : (
                    <ChevronRightIcon data-testid="workspaceVersionSelect" />
                )}
            </GridContainer>
            <Menu
                anchorEl={isMenuDisplayed}
                open={!!isMenuDisplayed}
                onClose={handleClose}
                className={classes.menuContainer}
                anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
            >
                {!!availableVersions && !!availableVersions.length ? (
                    availableVersions?.map(
                        ({
                            isSelected,
                            iterationName,
                            iterationId,
                            familyBaseElementId,
                        }) => {
                            return (
                                <MenuItem
                                    key={familyBaseElementId}
                                    onClick={handleMenuClick({
                                        iterationId,
                                        isSelected,
                                    })}
                                    title={iterationName}
                                    disabled={updateVersionLoading}
                                >
                                    <span className={classes.check}>
                                        {isSelected && <CheckIcon />}
                                    </span>
                                    <span className={classes.iterationName}>
                                        {iterationName}
                                    </span>
                                </MenuItem>
                            )
                        }
                    )
                ) : (
                    <MenuItem disabled>
                        Unexpected Error Occured. Please load again
                    </MenuItem>
                )}
            </Menu>
            <LoadingBackdrop isVisible={updateVersionLoading} />
            {showCautionDialog && (
                <ConfirmationDialog
                    openDialog={showCautionDialog}
                    dialogType="warning"
                    dialogHeading="Caution"
                    contentNode={dialogMessage}
                    confirmMessage={`Do you wish to continue and switch to another office master? `}
                    handleDialogClose={handleDialogClose}
                    dialogActionCallback={handleVersionUpdate(currentIteration)}
                    size="small"
                    cancelText="Cancel"
                    confirmText="Continue"
                />
            )}
        </div>
    )
}

export default WorkspaceVersionDropdown
